import { useQuery } from '@tanstack/react-query';
import { FileText, TrendingUp, Shield } from 'lucide-react';
import { billing } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { MetricCard } from '../components/MetricCard';
import type { Invoice } from '../types';

const STATUS_COLORS = {
  PAID: 'text-emerald-400',
  AUTHORISED: 'text-blue-400',
  OVERDUE: 'text-red-400',
  DRAFT: 'text-gray-500',
};

export default function BillingPage() {
  const { user } = useAuth();

  const { data: subscription, error: subscriptionError } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: billing.getSubscription,
  });

  const { data: invoices = [], error: invoicesError } = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: billing.listInvoices,
  });

  const error = (subscriptionError || invoicesError) as Error | null;
  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load billing data</p>
        <p className="text-xs text-gray-500">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary mt-4 text-sm"
        >
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">Billing & Subscription</h2>

      {/* Subscription overview */}
      {subscription && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard label="Plan" value={subscription.tier.toUpperCase()} />
            <MetricCard label="Devices" value={subscription.device_count ?? subscription.arm_count} />
            <MetricCard
              label="Pick Cycles"
              value={subscription.usage_cycles.toLocaleString()}
              sub={
                subscription.last_month_cycles > 0
                  ? `last month: ${subscription.last_month_cycles.toLocaleString()}`
                  : 'this billing period'
              }
            />
            <MetricCard
              label="Training Runs"
              value={`${subscription.usage_training_runs} / ${subscription.included_training_runs}`}
              sub={
                subscription.last_month_training_runs > 0
                  ? `last month: ${subscription.last_month_training_runs}`
                  : 'included this month'
              }
            />
          </div>

          {/* Active features */}
          <div className="card mb-6">
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <Shield size={14} /> Active Features
            </h3>
            <div className="flex flex-wrap gap-2">
              {subscription.features.length > 0 ? (
                subscription.features.map((feature) => (
                  <span key={feature} className="badge bg-kinetica-600/15 text-kinetica-400">
                    {feature.replace(/_/g, ' ')}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500">Base tier — local operation only</span>
              )}
            </div>
            {subscription.billing_status !== 'active' && (
              <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-400">
                Billing status: <strong className="capitalize">{subscription.billing_status}</strong>
                {subscription.billing_status === 'grace' &&
                  ' — please settle your invoice within 14 days to maintain cloud features.'}
                {subscription.billing_status === 'suspended' &&
                  ' — cloud features are suspended. Local operation continues. Contact support.'}
              </div>
            )}
          </div>
        </>
      )}

      {/* Invoice history */}
      <div className="card overflow-hidden p-0 overflow-x-auto">
        <div className="px-5 py-3 border-b border-surface-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
            <FileText size={14} /> Invoices
          </h3>
          {user?.org_type !== 'kinetica' && (
            <button
              onClick={() => billing.requestUpgrade('enterprise')}
              className="text-xs text-kinetica-400 hover:text-kinetica-300 flex items-center gap-1"
            >
              <TrendingUp size={12} /> Request Upgrade
            </button>
          )}
        </div>

        {invoices.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No invoices yet</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Date</th>
                <th className="table-header table-cell text-left">Invoice</th>
                <th className="table-header table-cell text-right">Total</th>
                <th className="table-header table-cell text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv: Invoice) => (
                <tr key={inv.invoice_id} className="hover:bg-surface-2/50">
                  <td className="table-cell text-xs text-gray-400">{inv.date}</td>
                  <td className="table-cell font-mono text-xs text-gray-300">{inv.invoice_id}</td>
                  <td className="table-cell text-right font-mono text-xs">
                    {inv.currency} {inv.total.toFixed(2)}
                  </td>
                  <td className="table-cell">
                    <span className={`text-xs font-semibold ${STATUS_COLORS[inv.status]}`}>
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
